<template>
    <div class="lg:max-w-5xl w-full grid gap-3 grid-cols-1 md:grid-cols-5 md:gap-x-5 lg:mx-auto">
        <div class="col-span-5 mt-5 sm:mt-0 mb-5">
            <nav aria-label="Progress">
                <ol role="list" class="space-y-4 md:flex md:space-y-0 md:space-x-8">
                    <li v-for="step in steps" :key="step.name" class="md:flex-1">
                        <router-link v-if="step.status === 'complete'" :to="step.href" class="group flex flex-col border-l-4 border-royal-blue-600 py-2 pl-4 hover:border-royal-blue-800 md:border-t-4 md:border-l-0 md:pt-4 md:pb-0 md:pl-0">
                            <span class="text-sm font-medium text-royal-blue-600 group-hover:text-royal-blue-800">{{ step.name }}</span>
                        </router-link>
                        <a v-else-if="step.status === 'current'" :href="step.href" class="flex flex-col border-l-4 border-royal-blue-600 py-2 pl-4 md:border-t-4 md:border-l-0 md:pt-4 md:pb-0 md:pl-0" aria-current="step">
                            <span class="text-sm font-medium text-royal-blue-600">{{ step.name }}</span>
                        </a>
                        <a v-else :href="step.href" class="group flex flex-col border-l-4 border-gray-200 py-2 pl-4 hover:border-gray-300 md:border-t-4 md:border-l-0 md:pt-4 md:pb-0 md:pl-0">
                            <span class="text-sm font-medium text-gray-500 group-hover:text-gray-700">{{ step.name }}</span>
                        </a>
                    </li>
                </ol>
            </nav>
        </div>
        <div class="col-span-5 md:col-span-3">
            <h3 class="text-base font-semibold text-gray-900 mb-3">{{ $t('checkout.payment_info') }}</h3>

            <form @submit.prevent="submit" class="bg-white rounded-md shadow-xs">
                <div class="p-4">
                    <p class="text-sm text-gray-500" v-if="wallet.length === 0">
                        {{ $t('checkout.wallet_empty_1') }}
                        {{ $t('checkout.wallet_empty_2') }} <router-link class="text-royal-blue-500" to="/subscription">{{ $t('checkout.wallet_empty_3') }}</router-link>.
                    </p>

                    <p class="text-sm text-gray-500" v-if="wallet.length !== 0">
                        {{ $t('checkout.wallet_tip') }}
                    </p>

                    <div class="mt-3" v-for="(walletItem, index) in wallet" :key="`wallet-item-${index}-${walletItem.key}`">
                        <label :for="`card-${walletItem.key}`" class="rounded-md bg-gray-100 px-3 py-2.5 flex items-center justify-between cursor-pointer">
                            <span class="sr-only">Visa</span>
                            <span class="flex items-center">
                                <svg v-if="walletItem.payment_system === 'visa'" class="h-8 w-auto sm:h-6 sm:shrink-0" viewBox="0 0 36 24" aria-hidden="true">
                                    <rect width="36" height="24" fill="#224DBA" rx="4" />
                                    <path fill="#fff" d="M10.925 15.673H8.874l-1.538-6c-.073-.276-.228-.52-.456-.635A6.575 6.575 0 005 8.403v-.231h3.304c.456 0 .798.347.855.75l.798 4.328 2.05-5.078h1.994l-3.076 7.5zm4.216 0h-1.937L14.8 8.172h1.937l-1.595 7.5zm4.101-5.422c.057-.404.399-.635.798-.635a3.54 3.54 0 011.88.346l.342-1.615A4.808 4.808 0 0020.496 8c-1.88 0-3.248 1.039-3.248 2.481 0 1.097.969 1.673 1.653 2.02.74.346 1.025.577.968.923 0 .519-.57.75-1.139.75a4.795 4.795 0 01-1.994-.462l-.342 1.616a5.48 5.48 0 002.108.404c2.108.057 3.418-.981 3.418-2.539 0-1.962-2.678-2.077-2.678-2.942zm9.457 5.422L27.16 8.172h-1.652a.858.858 0 00-.798.577l-2.848 6.924h1.994l.398-1.096h2.45l.228 1.096h1.766zm-2.905-5.482l.57 2.827h-1.596l1.026-2.827z" />
                                </svg>
                                <svg v-if="walletItem.payment_system === 'mc'" class="h-8 w-auto sm:h-6 sm:shrink-0" viewBox="0 0 39 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M38.6082 19.386V18.9218H38.4868L38.3465 19.2406L38.2068 18.9218H38.0847V19.386H38.171V19.0359L38.3022 19.338H38.3915L38.5227 19.0352V19.386H38.6082ZM37.8383 19.386V19.0009H37.9939V18.9225H37.5972V19.0009H37.7528V19.386H37.8383Z" fill="#F79410"/>
                                    <path d="M24.8127 21.3828H14.3124V2.56079H24.8127V21.3828Z" fill="#FF5F00"/>
                                    <path d="M14.9852 11.9703C14.9852 8.15215 16.7775 4.75106 19.5686 2.55927C17.5275 0.95652 14.9516 -0.000104904 12.1521 -0.000104904C5.52451 -0.000104904 0.1521 5.35913 0.1521 11.9703C0.1521 18.5814 5.52451 23.9407 12.1521 23.9407C14.9516 23.9407 17.5275 22.984 19.5686 21.3813C16.7775 19.1895 14.9852 15.7884 14.9852 11.9703Z" fill="#EB001B"/>
                                    <path d="M38.9744 11.9703C38.9744 18.5814 33.602 23.9407 26.9744 23.9407C24.1749 23.9407 21.599 22.984 19.5571 21.3813C22.349 19.1895 24.1413 15.7884 24.1413 11.9703C24.1413 8.15216 22.349 4.75107 19.5571 2.55928C21.599 0.95653 24.1749 -9.53674e-05 26.9744 -9.53674e-05C33.602 -9.53674e-05 38.9744 5.35914 38.9744 11.9703Z" fill="#F79E1B"/>
                                </svg>
                                <span class="mt-0 ml-3">
                                    <span class="text-sm font-medium text-gray-900">{{ walletItem.pan }}</span>
                                </span>
                            </span>
                            <span class="mt-0 sm:ml-6 shrink-0 flex items-center">
                                <input :id="`card-${walletItem.key}`" :aria-describedby="`card-${walletItem.key}-description`" name="card" type="radio" :value="walletItem.key" v-model="card" class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-royal-blue-600 checked:bg-royal-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-royal-blue-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden" />
                            </span>
                        </label>
                    </div>
                    <div class="mt-3">
                        <label :for="`card-other`" class="rounded-md bg-gray-100 px-3 py-2.5 flex items-center justify-between cursor-pointer">
                            <span class="flex items-center">
                                <span class="text-sm font-medium text-gray-900">{{ $t('checkout.other_card') }}</span>
                            </span>
                            <span class="mt-0 sm:ml-6 shrink-0 flex items-center">
                                <input :id="`card-other`" :aria-describedby="`card-other-description`" name="card" type="radio" value="other" v-model="card" class="relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white not-checked:before:hidden checked:border-royal-blue-600 checked:bg-royal-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-royal-blue-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden" />
                            </span>
                        </label>
                    </div>

                    <p class="mt-5 text-sm text-gray-700" v-if="(user && user.subscription_type) === 'free' || (user && selectedPlan && user.subscription_type !== selectedPlan.key)">
                        <span class="font-medium">{{ $t('checkout.payment_tip_1', {date: next_payment}) }}</span>
                    </p>
                    <p class="mt-5 text-sm text-gray-700" v-else>
                        <span class="font-medium">{{ $t('checkout.payment_tip_2') }}</span>
                    </p>

                    <p class="mt-3 text-sm text-gray-500" v-if="selectedPlan && selectedPlan.price === 0">
                        <span class="font-medium">{{ $t('checkout.payment_tip_3') }}</span>
                    </p>

                    <p class="mt-3 text-sm text-gray-500">
                        {{ $t('checkout.agreement_1') }} <a :href="publicUrl('agreement')" target="_blank" class="text-royal-blue-500">{{ $t('checkout.agreement_2') }}</a>.
                        {{ $t('checkout.agreement_3') }}
                        {{ $t('checkout.agreement_4') }} <router-link class="text-royal-blue-500" to="/subscription">{{ $t('checkout.agreement_5') }}</router-link>.
                    </p>
                </div>
            </form>
        </div>

        <div class="col-span-5 md:col-span-2" v-if="selectedPlan">
            <h3 class="text-base font-semibold text-gray-900 mb-3">{{ $t('checkout.subscription_details') }}</h3>

            <div :class="['relative block rounded-md bg-white p-4 shadow-xs focus:outline-hidden flex justify-between items-start']">
                <span class="flex items-center">
                    <span class="flex flex-col text-sm">
                        <span class="font-medium text-gray-900">
                            {{ selectedPlan.title }}
                        </span>
                        <span class="text-gray-500 flex flex-col">
                            <span class="block sm:inline">{{ Math.round(selectedPlan.limit / (1024 * 1024 * 1024) * 100) / 100 }} {{ $t('subscription_plan.gb_storage') }}</span>
                            <span v-if="selectedPlan.key !== 'free'" class="block sm:inline">{{ $t('subscription_plan.up_to_video') }}</span>
                            <span v-if="selectedPlan.key !== 'free'" class="block sm:inline">{{ $t('subscription_plan.unlimited_storage') }}</span>
                            <span v-if="selectedPlan.features && selectedPlan.features.face_recognition" class="block sm:inline">✨ {{ $t('subscription_plan.face_recognition') }}</span>
                            <span v-if="selectedPlan.key === 'free'" class="block sm:inline">{{ $t('subscription_plan.gallery_storage_60_days') }}</span>
                        </span>
                    </span>
                </span>
                <span class="flex text-sm sm:mt-0 sm:ml-4 flex-col sm:justify-center sm:text-right">
                    <span class="font-medium text-gray-900">
                        {{ formatPriceWithDecimals((totalPrice / selectedPeriod) / 100) }} {{ getCurrencySymbol() }}
                    </span>
                    <span class="sm:ml-1 text-gray-500 sm:ml-0">/ {{ $t('checkout.month') }}</span>
                </span>
            </div>

            <div class="border-l-4 border-red-400 bg-red-50 p-4 mt-3" v-if="promo_error">
                <div class="flex">
                    <div class="shrink-0">
                        <XCircleIcon class="size-5 text-red-400" aria-hidden="true" />
                    </div>
                    <div class="ml-3">
                        <p class="text-sm text-red-700">
                            {{ promo_error.message }}
                        </p>
                    </div>
                </div>
            </div>

            <form v-if="promo_info === null" @submit.prevent="usePromo" class="mt-3">
                <div class="bg-white rounded-md shadow-xs p-4 text-sm">
                    <span class="font-medium text-gray-900">{{ $t('checkout.promocode') }}</span>

                    <div>
                        <div class="mt-2 flex">
                            <div class="-mr-px grid grow grid-cols-1 focus-within:relative">
                                <input type="text" v-model="promo" name="code" class="col-start-1 row-start-1 block w-full rounded-l-md bg-white py-1.5 pr-3 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-royal-blue-600 sm:pl-3 sm:text-sm/6" :placeholder="$t('checkout.enter_promo')" />
                            </div>
                            <button :disabled="promo === null || promo === ''" type="submit" class="flex shrink-0 items-center gap-x-1.5 rounded-r-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 outline-1 -outline-offset-1 outline-gray-300 hover:bg-gray-50 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-royal-blue-600 cursor-pointer">
                                {{ $t('checkout.use_promo') }}
                            </button>
                        </div>
                    </div>
                </div>
            </form>

            <div class="mt-3" v-if="promo_info !== null">
                <div class="bg-white rounded-md shadow-xs p-4 text-sm">
                    <span class="font-medium text-gray-900">{{ $t('checkout.promocode') }} "{{ promo_info.title }}" {{ $t('checkout.promo_gives') }}:</span>
                    <ul class="my-2 text-gray-500">
                        <li v-for="(preference, index) in promo_info.preferences_titles" :key="`preference-promo-item-${index}`">{{ preference }}</li>
                    </ul>

                    <a href="#" @click.prevent="removePromo" class="text-red-700">{{ $t('checkout.delete_promo') }}</a>
                </div>
            </div>

            <!-- Currency conversion notification -->
            <div class="border-l-4 border-blue-400 bg-blue-50 p-4 mt-3" v-if="currencyPrice && currencyRate && currencyRate !== 0">
                <div class="flex">
                    <div class="shrink-0">
                        <svg class="size-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                        </svg>
                    </div>
                    <div class="ml-3">
                        <p class="text-sm text-blue-700">
                            {{ $t('checkout.currency_notification', {
                                uahPrice: (currencyPrice / 100).toFixed(2),
                                rate: currencyRate.toFixed(2)
                            }) }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Black Friday Upgrade Banner -->
            <div class="border-l-4 border-purple-500 bg-gradient-to-r from-purple-50 to-pink-50 p-4 mt-3" v-if="blackFridayUpgrade && blackFridayUpgrade.is_active">
                <div class="flex">
                    <div class="shrink-0">
                        <span class="text-2xl">🛍️</span>
                    </div>
                    <div class="ml-3 flex-1">
                        <p class="text-sm font-bold text-purple-900">
                            ⚡ {{ $t('subscription.black_friday.checkout_title') }}
                        </p>
                        <p class="text-sm text-purple-800 mt-1" v-html="$t('subscription.black_friday.checkout_description', {
                            discount: blackFridayUpgrade.discount_percent,
                            bonusDays: blackFridayUpgrade.bonus_days
                        })">
                        </p>
                        <a :href="blackFridayTermsUrl" target="_blank" class="ml-2 text-sm font-medium text-purple-700 hover:text-purple-900 hover:underline transition-all cursor-pointer">
                            {{ $t('subscription.black_friday.terms_button') }}
                        </a>
                    </div>
                </div>
            </div>

            <div class="mt-3">
                <div class="flex items-center justify-between lg:px-4 text-sm">
                    <span class="font-medium text-gray-900">{{ $t('checkout.total_amount') }}</span>
                    <span class="text-gray-500">
                        <s v-if="oldPrice !== 0 && oldPrice !== null && totalPrice != oldPrice" class="me-2">{{ formatPriceWithDecimals(oldPrice / 100) }} {{ getCurrencySymbol() }}</s>
                        <span class="text-gray-900">{{ formatPriceWithDecimals(totalPrice / 100) }} {{ getCurrencySymbol() }} </span>/ {{ periodTitle }}
                    </span>
                </div>
            </div>

            <div class="mt-3">
                <button type="button" @click.prevent="submit" class="cursor-pointer w-full inline-flex justify-center rounded-md border border-transparent bg-royal-blue-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-royal-blue-700 focus:outline-none focus:ring-2 focus:ring-royal-blue-500 focus:ring-offset-2">
                    <span class="loader" v-if="isLoadingCheckout"></span>
                    <span v-else>{{ $t('checkout.pay') }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import {mapActions, mapGetters} from "vuex";
import {checkout, checkoutInfo} from "@/data/requests/subscription";
import {SubscriptionPlan} from "@/types/UserType";
import {XCircleIcon} from "@heroicons/vue/20/solid";

export default defineComponent({
    name: "UserSubscriptionCheckoutPage",
    components: {XCircleIcon},

    data() {
        return {
            steps: [
                { name: this.$t('checkout.step_1'), href: '/subscription', status: 'complete' },
                { name: this.$t('checkout.step_2'), href: '#', status: 'current' },
                { name: this.$t('checkout.step_3'), href: '#', status: 'upcoming' },
            ],

            selectedPlan: null as SubscriptionPlan|null,

            next_payment: null as string|null,

            promo_error: null as object|null,

            promo_info: null as object|null,

            promo: null,

            totalPrice: 0 as number|null,
            oldPrice: 0 as number|null,

            currencyPrice: 0 as number|null,
            currencyRate: 0 as number|null,

            blackFridayUpgrade: null as {is_active: boolean, bonus_days: number, discount_percent: number}|null,

            card: "other",

            isLoadingWallet: false,

            isLoadingCheckout: false,
        };
    },

    computed: {
        ...mapGetters({
            wallet: "user/wallet",
            user: "user/user",
        }),

        selectedPlanKey(): string|null {
            const key = this.$route.params.key;

            if(key) {
                return key as string;
            }

            return null;
        },

        selectedPeriod(): number {
            const period = this.$route.query.p;

            if(period) {
                return parseInt(period as any);
            }

            return 1;
        },

        periodTitle(): string {
            if(this.selectedPeriod == 1) {
                return this.$t('checkout.month');
            }

            return this.selectedPeriod + " " + this.$t('checkout.months');
        }
    },

    mounted() {
        this.loadWallet();
        this.recalculate();
        
        // Перевіряємо при зміні роута
        this.$watch(() => this.$route.query, () => {
            this.recalculate();
        }, { deep: true });
    },

    methods: {
        ...mapActions({
            fetchWallet: "user/fetchWallet",

            errorAlert: "alerts/error",
            successAlert: "alerts/success",
        }),

        submit() {
            if(this.isLoadingCheckout) {
                return;
            }

            this.isLoadingCheckout = true;

            const isBlackFridayUpgrade = this.$route.query.black_friday === 'true' && this.selectedPeriod === 1;

            checkout(this.selectedPlanKey as string, this.promo, this.card, this.selectedPeriod, isBlackFridayUpgrade, (data) => {
                if(data.token_payment) {
                    // this.successAlert({
                    //     message: "Підписку успішно оплачено"
                    // });

                    this.$router.push('/thank-you');

                    this.isLoadingCheckout = false;
                } else {
                    location.href = data.redirect_to as string;

                    this.isLoadingCheckout = false;
                }
            }, () => {
                this.isLoadingCheckout = false;

                this.errorAlert({
                    message: this.$t('checkout.error'),
                });
            });
        },

        usePromo() {
            this.recalculate();
        },

        removePromo() {
            this.promo = null;

            this.recalculate();
        },

        recalculate() {
            // Перевіряємо чи це Black Friday апгрейд
            const isBlackFridayUpgrade = this.$route.query.black_friday === 'true';
            
            console.log('Checkout recalculate - isBlackFridayUpgrade:', isBlackFridayUpgrade);
            
            checkoutInfo(this.selectedPlanKey as string, this.promo, this.selectedPeriod, (data) => {
                console.log('Checkout info response:', data);
                this.selectedPlan = data.subscription;
                this.promo_info = data.promo_info;
                this.promo_error = data.promo_error;
                this.next_payment = data.next_payment;
                this.oldPrice = data.old_price;
                this.totalPrice = data.total_price;
                this.currencyPrice = data.currency_price ?? null;
                this.currencyRate = data.currency_rate ?? null;
                this.blackFridayUpgrade = data.black_friday_upgrade ?? null;
                console.log('Black Friday upgrade data:', this.blackFridayUpgrade);
            }, () => {}, isBlackFridayUpgrade)
        },

        loadWallet() {
            this.isLoadingWallet = true;

            this.fetchWallet()
                .then(() => {
                    this.isLoadingWallet = false;

                    if(this.wallet.length !== 0) {
                        this.card = this.wallet[0].key;
                    }
                })
                .catch(({data}) => {
                    this.errorAlert({
                        message: data.message,
                    })

                    this.isLoadingWallet = false;
                });
        },

        publicUrl(url: string): string {
            return process.env.VUE_APP_PUBLIC_URL + url;
        },

        getPriceForCurrency(item: SubscriptionPlan): string {
            if(item.key === 'free') {
                return '0';
            }

            const price = this.user?.currency === 'EUR' ? item.price_eur : item.price;
            // Both EUR and UAH prices are stored in smallest units (cents/kopecks)
            // So we need to divide by 100 for both currencies
            return (price / 100).toString();
        },

        getCurrencySymbol(): string {
            return this.user?.currency === 'EUR' ? this.$t('subscription_plan.eur') : this.$t('subscription_plan.uah');
        },

        formatPriceWithDecimals(price: number): string {
            // Для EUR показуємо 2 десяткові знаки, для UAH - 0
            if (this.user?.currency === 'EUR') {
                return price.toFixed(2);
            }
            return Math.round(price).toString();
        },

        blackFridayTermsUrl(): string {
            const locale = this.$i18n.locale === 'en' ? 'en' : 'uk';
            return locale === 'en' 
                ? 'https://gallera.io/en/blackfriday2025'
                : 'https://gallera.io/blackfriday2025';
        },
    }
});
</script>

<style scoped lang="scss">
.loader {
    display: block;
    margin: 6.53px auto;
    width: 10px;
    aspect-ratio: 1;
    border-radius: 50%;
    animation: l5 1s infinite linear alternate;
}

@keyframes l5 {
    0%  {box-shadow: 20px 0 #fff, -20px 0 #ccc;background: #fff }
    33% {box-shadow: 20px 0 #fff, -20px 0 #ccc;background: #ccc}
    66% {box-shadow: 20px 0 #ccc,-20px 0 #fff; background: #ccc}
    100%{box-shadow: 20px 0 #ccc,-20px 0 #fff; background: #fff }
}
</style>